import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import { useSmsLogs } from "../../../hooks/useSmsLogs";
import { useUsers } from "../../../hooks/useUsers";
import {
  clearSessionUser,
  mustForcePasswordChange,
  readSessionUser,
} from "../../../lib/authSession";
import {
  getDeliveryAssignments,
  rehydrateDeliveryAssignments,
  subscribeDeliveryAssignments,
  type DeliveryAssignment,
} from "../../../lib/deliveryStore";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import { sendManualSms } from "../../../lib/smsService";
import {
  deriveSlaughterOpStatus,
  getSlaughterOps,
  subscribeSlaughterOps,
  type SlaughterOpStatus,
} from "../../../lib/slaughterOpsStore";
import { normalizePhone, normalizeText } from "../../../store/usersStore";
import type { AnimalItem, ShareItem } from "../../../mocks/animals";
import type { CustomerItem } from "../../../mocks/customers";

const currency = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  maximumFractionDigits: 0,
});

type ConsentFilter = "Tümü" | "Onaylı" | "Onaysız" | "Seçim Yok";
type BalanceFilter = "Tümü" | "Bakiyesi Olan" | "Bakiye Yok";
type DeliveryLabel = "Yok" | "Hazır" | "Taşıma Halinde" | "Teslim Edildi";
type SlaughterLabel =
  | "Yok"
  | "Kesim Bekliyor"
  | "Kesime Girdi"
  | "Kesim Yapıldı"
  | "Parçalanıyor"
  | "Hazır";

interface CustomerSmsRow {
  customerId: number;
  customerName: string;
  phone: string;
  operationalOptIn: boolean | null;
  operationalAt?: string;
  marketingOptIn: boolean | null;
  marketingAt?: string;
  slaughterStatus: SlaughterLabel;
  deliveryStatus: DeliveryLabel;
  balance: number;
  lastSmsAt?: string;
  lastSmsEvent?: string;
}

interface FilterState {
  search: string;
  operational: ConsentFilter;
  marketing: ConsentFilter;
  balance: BalanceFilter;
}

interface ExportSmsCustomerRow {
  musteri: string;
  telefon: string;
  operasyonelIzin: string;
  reklamIzin: string;
  kesimDurumu: string;
  tasimaDurumu: string;
  bakiye: number;
  sonSmsTarihi: string;
  sonSmsOlayi: string;
}

const EMPTY_FILTERS: FilterState = {
  search: "",
  operational: "Tümü",
  marketing: "Tümü",
  balance: "Tümü",
};

function formatDateTime(value?: string) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function findCustomerAccount(
  customer: CustomerItem,
  users: ReturnType<typeof useUsers>["users"]
) {
  return (
    users.find(
      (user) =>
        user.rol === "Müşteri" &&
        (user.customerId === customer.id ||
          normalizePhone(user.telefon) === normalizePhone(customer.telefon) ||
          normalizeText(user.adSoyad) === normalizeText(customer.adSoyad))
    ) ?? null
  );
}

function customerMatchesShare(customer: CustomerItem, share: ShareItem) {
  const customerPhone = normalizePhone(customer.telefon);
  const sharePhone = normalizePhone(share.telefon);

  if (customerPhone && sharePhone && customerPhone === sharePhone) {
    return true;
  }

  return normalizeText(customer.adSoyad) === normalizeText(share.musteriAdi);
}

function getCustomerShares(customer: CustomerItem, animals: AnimalItem[]) {
  return animals.flatMap((animal) =>
    animal.hisseler
      .filter((share) => customerMatchesShare(customer, share))
      .map((share) => ({ animal, share }))
  );
}

function toConsentFilter(value: boolean | null): ConsentFilter {
  if (value === true) return "Onaylı";
  if (value === false) return "Onaysız";
  return "Seçim Yok";
}

function getSlaughterLabel(
  links: ReturnType<typeof getCustomerShares>,
  slaughterMap: Map<number, SlaughterOpStatus>
): SlaughterLabel {
  if (links.length === 0) return "Yok";

  const rank: Record<SlaughterLabel, number> = {
    Yok: 0,
    "Kesim Bekliyor": 1,
    "Kesime Girdi": 2,
    "Kesim Yapıldı": 3,
    Parçalanıyor: 4,
    Hazır: 5,
  };

  let best: SlaughterLabel = "Yok";

  links.forEach(({ animal }) => {
    const status = deriveSlaughterOpStatus(animal, slaughterMap);

    const label: SlaughterLabel =
      status === "done"
        ? "Hazır"
        : status === "packing"
        ? "Parçalanıyor"
        : status === "cut"
        ? "Kesim Yapıldı"
        : status === "entered"
        ? "Kesime Girdi"
        : "Kesim Bekliyor";

    if (rank[label] > rank[best]) {
      best = label;
    }
  });

  return best;
}

function getDeliveryLabel(
  links: ReturnType<typeof getCustomerShares>,
  deliveries: DeliveryAssignment[]
): DeliveryLabel {
  if (links.length === 0) return "Yok";

  const shareIds = new Set(links.map(({ share }) => share.id));
  const related = deliveries.filter((item) => shareIds.has(item.shareId));

  if (related.some((item) => item.status === "Taşıma Halinde")) {
    return "Taşıma Halinde";
  }

  if (related.some((item) => item.status === "Hazır")) {
    return "Hazır";
  }

  if (related.length > 0 && related.every((item) => item.status === "Teslim Edildi")) {
    return "Teslim Edildi";
  }

  if (
    links.some(
      ({ animal }) => animal.tamamlandi || animal.durum === "Teslim Edildi"
    )
  ) {
    return "Teslim Edildi";
  }

  if (links.some(({ animal }) => animal.durum === "Kesildi")) {
    return "Hazır";
  }

  return "Yok";
}

function getLastSmsInfo(
  customer: CustomerItem,
  logs: ReturnType<typeof useSmsLogs>["logs"]
) {
  const phone = normalizePhone(customer.telefon);

  const related = logs
    .filter((item) => {
      if (item.customerId === customer.id) return true;
      return normalizePhone(item.phone) === phone;
    })
    .sort((a, b) => {
      const aTime = a.sentAt || a.createdAt;
      const bTime = b.sentAt || b.createdAt;
      return bTime.localeCompare(aTime);
    });

  const latest = related[0];

  return {
    lastSmsAt: latest ? latest.sentAt || latest.createdAt : undefined,
    lastSmsEvent: latest?.eventType,
  };
}

function getConsentBadge(value: boolean | null) {
  if (value === true) {
    return "bg-farm-green-pale text-farm-green border-farm-green/30";
  }

  if (value === false) {
    return "bg-red-50 text-red-600 border-red-200";
  }

  return "bg-gray-100 text-gray-600 border-gray-300";
}

function getStatusBadge(status: SlaughterLabel | DeliveryLabel) {
  if (status === "Teslim Edildi" || status === "Hazır") {
    return "bg-farm-green-pale text-farm-green border-farm-green/30";
  }

  if (status === "Taşıma Halinde" || status === "Parçalanıyor") {
    return "bg-sky-50 text-sky-700 border-sky-200";
  }

  if (status === "Kesim Yapıldı" || status === "Kesime Girdi") {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }

  if (status === "Kesim Bekliyor") {
    return "bg-farm-earth-pale text-farm-earth border-farm-earth/20";
  }

  return "bg-gray-100 text-gray-600 border-gray-300";
}

export default function SmsMusteriPage() {
  const navigate = useNavigate();
  const { animals } = useAnimals();
  const { customers } = useCustomers();
  const { users } = useUsers();
  const { logs } = useSmsLogs();

  const [ready, setReady] = useState(false);
  const [opsVersion, setOpsVersion] = useState(0);
  const [deliveryVersion, setDeliveryVersion] = useState(0);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [message, setMessage] = useState("");
  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const sessionUser = readSessionUser();

    if (!sessionUser) {
      navigate("/giris", { replace: true });
      return;
    }

    const currentUser = users.find((item) => item.id === sessionUser.id);

    if (!currentUser || currentUser.durum !== "Aktif") {
      clearSessionUser();
      navigate("/giris", { replace: true });
      return;
    }

    if (mustForcePasswordChange(currentUser)) {
      navigate("/sifre-degistir", { replace: true });
      return;
    }

    if (currentUser.rol !== "Admin" && currentUser.rol !== "Muhasebe") {
      navigate("/panel/hayvanlar", { replace: true });
      return;
    }

    setReady(true);
  }, [navigate, users]);

  useEffect(() => {
    void rehydrateDeliveryAssignments();

    const unsubscribe = subscribeDeliveryAssignments(() => {
      setDeliveryVersion((value) => value + 1);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeSlaughterOps(() => {
      setOpsVersion((value) => value + 1);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const rows = useMemo<CustomerSmsRow[]>(() => {
    const slaughterMap = new Map(
      getSlaughterOps().map((item) => [item.animalId, item.status])
    );
    const deliveries = getDeliveryAssignments();

    return customers
      .map((customer) => {
        const account = findCustomerAccount(customer, users);
        const links = getCustomerShares(customer, animals);
        const balance = links.reduce((sum, item) => sum + item.share.kalan, 0);
        const lastSmsInfo = getLastSmsInfo(customer, logs);

        return {
          customerId: customer.id,
          customerName: customer.adSoyad,
          phone: customer.telefon,
          operationalOptIn: account?.smsOperationalOptIn ?? null,
          operationalAt: account?.smsOperationalOptInAt,
          marketingOptIn: account?.smsMarketingOptIn ?? null,
          marketingAt: account?.smsMarketingOptInAt,
          slaughterStatus: getSlaughterLabel(links, slaughterMap),
          deliveryStatus: getDeliveryLabel(links, deliveries),
          balance,
          lastSmsAt: lastSmsInfo.lastSmsAt,
          lastSmsEvent: lastSmsInfo.lastSmsEvent,
        };
      })
      .sort((a, b) => a.customerName.localeCompare(b.customerName, "tr"));
  }, [animals, customers, users, logs, opsVersion, deliveryVersion]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const query = normalizeText(filters.search);

      if (query) {
        const searchPool = [
          normalizeText(row.customerName),
          normalizePhone(row.phone),
          normalizeText(row.lastSmsEvent),
        ];

        if (!searchPool.some((item) => item.includes(query))) {
          return false;
        }
      }

      if (
        filters.operational !== "Tümü" &&
        toConsentFilter(row.operationalOptIn) !== filters.operational
      ) {
        return false;
      }

      if (
        filters.marketing !== "Tümü" &&
        toConsentFilter(row.marketingOptIn) !== filters.marketing
      ) {
        return false;
      }

      if (filters.balance === "Bakiyesi Olan" && row.balance <= 0) {
        return false;
      }

      if (filters.balance === "Bakiye Yok" && row.balance > 0) {
        return false;
      }

      return true;
    });
  }, [rows, filters]);

  const selectedRows = useMemo(() => {
    return rows.filter((row) => selectedIds[row.customerId]);
  }, [rows, selectedIds]);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      selected: selectedRows.length,
      operationalApproved: rows.filter((row) => row.operationalOptIn === true).length,
      marketingApproved: rows.filter((row) => row.marketingOptIn === true).length,
    };
  }, [rows, selectedRows]);

  function setFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function toggleRow(customerId: number) {
    setSelectedIds((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  }

  function selectRows(nextRows: CustomerSmsRow[]) {
    const next: Record<number, boolean> = {};
    nextRows.forEach((row) => {
      next[row.customerId] = true;
    });
    setSelectedIds(next);
  }

  function clearSelection() {
    setSelectedIds({});
  }

  function exportCurrent() {
    const exportRows: ExportSmsCustomerRow[] = filteredRows.map((row) => ({
      musteri: row.customerName,
      telefon: row.phone,
      operasyonelIzin: toConsentFilter(row.operationalOptIn),
      reklamIzin: toConsentFilter(row.marketingOptIn),
      kesimDurumu: row.slaughterStatus,
      tasimaDurumu: row.deliveryStatus,
      bakiye: row.balance,
      sonSmsTarihi: formatDateTime(row.lastSmsAt),
      sonSmsOlayi: row.lastSmsEvent ?? "",
    }));

    const columns: ExportColumn<ExportSmsCustomerRow>[] = [
      { key: "musteri", label: "Müşteri" },
      { key: "telefon", label: "Telefon" },
      { key: "operasyonelIzin", label: "Operasyonel İzin" },
      { key: "reklamIzin", label: "Reklam İzni" },
      { key: "kesimDurumu", label: "Kesim Durumu" },
      { key: "tasimaDurumu", label: "Taşıma Durumu" },
      { key: "bakiye", label: "Bakiye" },
      { key: "sonSmsTarihi", label: "Son SMS Tarihi" },
      { key: "sonSmsOlayi", label: "Son SMS Olayı" },
    ];

    exportRowsToCsv(
      `sms-musteri-${new Date().toISOString().slice(0, 10)}`,
      exportRows,
      columns
    );
  }

  function handleSend() {
    const content = message.trim();

    if (!content) {
      window.alert("Mesaj boş bırakılamaz.");
      return;
    }

    if (selectedRows.length === 0) {
      window.alert("En az 1 müşteri seçmelisiniz.");
      return;
    }

    const confirmed = window.confirm(
      `${selectedRows.length} müşteriye operasyonel SMS logu oluşturulacak. Devam edilsin mi?`
    );

    if (!confirmed) return;

    setSending(true);

    setTimeout(() => {
      selectedRows.forEach((row) => {
        void sendManualSms({
          channel: "operational",
          eventType: "bulk_operational",
          phone: row.phone,
          customerId: row.customerId,
          userOptIn: row.operationalOptIn,
          message: content,
        });
      });

      setSending(false);
      setMessage("");
      window.alert("Toplu operasyonel SMS logları oluşturuldu.");
    }, 150);
  }

  if (!ready) return null;

  return (
    <div className="min-h-full bg-farm-cream font-body">
      <div className="max-w-[1800px] mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              SMS Müşteri
            </h1>
            <p className="text-xs text-farm-muted mt-0.5">
              Müşteri izinleri, son SMS tarihi ve toplu operasyonel gönderim ekranı
            </p>
          </div>

          <button
            type="button"
            onClick={exportCurrent}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
          >
            <i className="ri-file-excel-2-line text-base" />
            Excel Çıktısı
          </button>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
          {[
            {
              label: "Toplam Müşteri",
              value: stats.total,
              icon: "ri-group-line",
              color: "text-farm-brown",
              bg: "bg-farm-earth-pale",
            },
            {
              label: "Seçili",
              value: stats.selected,
              icon: "ri-checkbox-multiple-line",
              color: "text-farm-earth",
              bg: "bg-farm-earth-pale",
            },
            {
              label: "Operasyonel Onaylı",
              value: stats.operationalApproved,
              icon: "ri-message-2-line",
              color: "text-farm-green",
              bg: "bg-farm-green-pale",
            },
            {
              label: "Reklam Onaylı",
              value: stats.marketingApproved,
              icon: "ri-megaphone-line",
              color: "text-fuchsia-700",
              bg: "bg-fuchsia-50",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-farm-warm border border-farm-border rounded-farm px-3 py-3 flex items-center gap-3"
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.bg} ${item.color}`}
              >
                <i className={`${item.icon} text-base`} />
              </div>
              <div>
                <p className={`text-xl font-bold leading-none ${item.color}`}>
                  {item.value}
                </p>
                <p className="text-[10px] text-farm-muted mt-0.5">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-farm-warm border border-farm-border rounded-farm p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
            <input
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              placeholder="Müşteri, telefon veya son olay ara..."
              className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
            />

            <select
              value={filters.operational}
              onChange={(e) =>
                setFilter("operational", e.target.value as ConsentFilter)
              }
              className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
            >
              <option value="Tümü">Operasyonel: Tümü</option>
              <option value="Onaylı">Onaylı</option>
              <option value="Onaysız">Onaysız</option>
              <option value="Seçim Yok">Seçim Yok</option>
            </select>

            <select
              value={filters.marketing}
              onChange={(e) =>
                setFilter("marketing", e.target.value as ConsentFilter)
              }
              className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
            >
              <option value="Tümü">Reklam: Tümü</option>
              <option value="Onaylı">Onaylı</option>
              <option value="Onaysız">Onaysız</option>
              <option value="Seçim Yok">Seçim Yok</option>
            </select>

            <select
              value={filters.balance}
              onChange={(e) =>
                setFilter("balance", e.target.value as BalanceFilter)
              }
              className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
            >
              <option value="Tümü">Bakiye: Tümü</option>
              <option value="Bakiyesi Olan">Bakiyesi Olan</option>
              <option value="Bakiye Yok">Bakiye Yok</option>
            </select>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => selectRows(filteredRows)}
              className="h-9 px-3 rounded-full border border-farm-border bg-farm-cream text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
            >
              Görünenleri Seç
            </button>

            <button
              type="button"
              onClick={clearSelection}
              className="h-9 px-3 rounded-full border border-farm-border bg-farm-cream text-farm-muted text-xs font-semibold hover:border-farm-brown/40"
            >
              Seçimi Temizle
            </button>

            {(filters.search ||
              filters.operational !== "Tümü" ||
              filters.marketing !== "Tümü" ||
              filters.balance !== "Tümü") && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="h-9 px-3 rounded-full border border-farm-border bg-white text-farm-muted text-xs font-semibold hover:border-farm-earth/40"
              >
                Filtreleri Temizle
              </button>
            )}
          </div>
        </div>

        <div className="bg-farm-warm border border-farm-border rounded-farm p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-farm-brown">
                Toplu Operasyonel Mesaj
              </p>
              <p className="text-[11px] text-farm-muted mt-0.5">
                Seçili müşterilere operasyonel SMS logu oluşturur
              </p>
            </div>

            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-farm-earth-pale text-farm-earth text-[10px] font-semibold">
              Seçili: {selectedRows.length}
            </span>
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth resize-none"
            placeholder="Toplu operasyonel mesaj metni..."
          />

          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="w-full md:w-auto h-10 px-5 rounded-full bg-farm-earth text-white text-sm font-semibold hover:bg-farm-brown disabled:opacity-60"
          >
            {sending ? "Gönderiliyor..." : "Seçili Müşterilere Gönder"}
          </button>
        </div>

        <div className="bg-farm-warm border border-farm-border rounded-farm overflow-hidden">
          <div
            className="hidden xl:grid bg-farm-cream border-b border-farm-border text-[10px] font-semibold text-farm-muted uppercase tracking-wide"
            style={{
              gridTemplateColumns:
                "48px 1.2fr 130px 120px 120px 140px 140px 110px 140px",
            }}
          >
            <div className="px-3 py-2 text-center">Seç</div>
            <div className="px-3 py-2">Müşteri</div>
            <div className="px-3 py-2">Telefon</div>
            <div className="px-3 py-2">Operasyonel</div>
            <div className="px-3 py-2">Reklam</div>
            <div className="px-3 py-2">Kesim</div>
            <div className="px-3 py-2">Taşıma</div>
            <div className="px-3 py-2 text-right">Bakiye</div>
            <div className="px-3 py-2">Son SMS</div>
          </div>

          {filteredRows.length === 0 ? (
            <div className="py-12 text-center text-sm text-farm-muted">
              Kayıt bulunamadı
            </div>
          ) : (
            filteredRows.map((row) => {
              const selected = !!selectedIds[row.customerId];

              return (
                <div
                  key={row.customerId}
                  className="border-b border-farm-border last:border-b-0"
                >
                  <div
                    className="hidden xl:grid items-center"
                    style={{
                      gridTemplateColumns:
                        "48px 1.2fr 130px 120px 120px 140px 140px 110px 140px",
                    }}
                  >
                    <div className="px-3 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleRow(row.customerId)}
                        className="w-4 h-4 accent-[#8b5a2b]"
                      />
                    </div>

                    <div className="px-3 py-3">
                      <p className="text-sm font-semibold text-farm-brown">
                        {row.customerName}
                      </p>
                      <p className="text-[11px] text-farm-muted mt-0.5">
                        Son olay: {row.lastSmsEvent || "—"}
                      </p>
                    </div>

                    <div className="px-3 py-3 text-xs text-farm-brown">
                      {row.phone}
                    </div>

                    <div className="px-3 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getConsentBadge(
                          row.operationalOptIn
                        )}`}
                      >
                        {toConsentFilter(row.operationalOptIn)}
                      </span>
                    </div>

                    <div className="px-3 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getConsentBadge(
                          row.marketingOptIn
                        )}`}
                      >
                        {toConsentFilter(row.marketingOptIn)}
                      </span>
                    </div>

                    <div className="px-3 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getStatusBadge(
                          row.slaughterStatus
                        )}`}
                      >
                        {row.slaughterStatus}
                      </span>
                    </div>

                    <div className="px-3 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getStatusBadge(
                          row.deliveryStatus
                        )}`}
                      >
                        {row.deliveryStatus}
                      </span>
                    </div>

                    <div className="px-3 py-3 text-right text-sm font-semibold text-farm-brown">
                      {currency.format(row.balance)}
                    </div>

                    <div className="px-3 py-3 text-[11px] text-farm-muted">
                      {formatDateTime(row.lastSmsAt)}
                    </div>
                  </div>

                  <div className="xl:hidden px-3 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRow(row.customerId)}
                          className="w-4 h-4 mt-0.5 accent-[#8b5a2b]"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-farm-brown">
                            {row.customerName}
                          </p>
                          <p className="text-xs text-farm-muted mt-0.5">
                            {row.phone}
                          </p>
                        </div>
                      </div>

                      <p className="text-sm font-semibold text-farm-brown">
                        {currency.format(row.balance)}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getConsentBadge(
                          row.operationalOptIn
                        )}`}
                      >
                        Operasyonel: {toConsentFilter(row.operationalOptIn)}
                      </span>

                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getConsentBadge(
                          row.marketingOptIn
                        )}`}
                      >
                        Reklam: {toConsentFilter(row.marketingOptIn)}
                      </span>

                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getStatusBadge(
                          row.slaughterStatus
                        )}`}
                      >
                        {row.slaughterStatus}
                      </span>

                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getStatusBadge(
                          row.deliveryStatus
                        )}`}
                      >
                        {row.deliveryStatus}
                      </span>
                    </div>

                    <p className="text-[11px] text-farm-muted">
                      Son SMS: {formatDateTime(row.lastSmsAt)} · {row.lastSmsEvent || "—"}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}